import i18n from '@/translations'
import { useFeatureFlag } from '@/composables/useFeatureFlag'
import type { MissionType } from '@/types/types'
import { safeHttpUrl } from './brandAppUrl'
import type { BrandAppFont } from './brandAppFonts'

export type HomeBlockId = 'offers' | 'news' | 'menu' | 'social'

export interface HomeBlockDef {
  id: HomeBlockId
  label: string
  defaultTitle: string
}

export const HOME_BLOCKS: HomeBlockDef[] = [
  {
    id: 'offers',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.offers.label')
    },
    get defaultTitle() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.offers.defaultTitle')
    }
  },
  {
    id: 'news',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.news.label')
    },
    get defaultTitle() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.news.defaultTitle')
    }
  },
  {
    id: 'menu',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.menu.label')
    },
    get defaultTitle() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.menu.defaultTitle')
    }
  },
  {
    id: 'social',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.social.label')
    },
    get defaultTitle() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.homeBlocks.social.defaultTitle')
    }
  }
]

export const HOME_BLOCK_TITLE_MAX_LENGTH = 40

export const DEFAULT_HOME_BLOCKS: HomeBlockId[] = ['offers', 'news', 'menu']

export const COMING_SOON_HOME_BLOCKS: HomeBlockId[] = []

export const isComingSoonHomeBlock = (id: string): boolean => (COMING_SOON_HOME_BLOCKS as string[]).includes(id)

const hiddenHomeBlocks = (): HomeBlockId[] => {
  const { flags, error } = useFeatureFlag()

  return error.value || flags.value['offer-engine']?.enabled ? [] : ['offers']
}

export const isHiddenHomeBlock = (id: string): boolean => (hiddenHomeBlocks() as string[]).includes(id)

export const isPublishableHomeBlock = (id: string): boolean => !isComingSoonHomeBlock(id) && !isHiddenHomeBlock(id)

export const visibleHomeBlocks = (): HomeBlockDef[] => HOME_BLOCKS.filter((block) => !isHiddenHomeBlock(block.id))

export interface BrandAppHomeDressing {
  homeTextColor?: string
  sectionSurface?: string
  navPillColor?: string
  navPillActiveColor?: string
  navPillTextColor?: string
  navPillActiveTextColor?: string
}

export interface BrandAppOfferCard {
  offerId: number
  publicId?: string
  rewardId: number
  image: string
  title: string
  description: string
  ctaLabel: string
  isVisible?: boolean
  isPlaceholder?: boolean
}

export const isContentVisible = (item: { isVisible?: boolean }) => item.isVisible !== false

export interface BrandAppHome {
  teasingHeadline: string
  heroImage: string
  menuImage: string
  menuStoreUrls: Record<number, string>
  blocks: HomeBlockId[]
  blockTitles: Partial<Record<HomeBlockId, string>>
  offerIds: number[]
  offerCards: Partial<Record<number, BrandAppOfferCard>>
  actus: BrandAppNews[]
  dressing: BrandAppHomeDressing
}

export const DEFAULT_HOME: BrandAppHome = {
  teasingHeadline: '',
  heroImage: '',
  menuImage: '',
  menuStoreUrls: {},
  blocks: [...DEFAULT_HOME_BLOCKS],
  blockTitles: {},
  offerIds: [],
  offerCards: {},
  actus: [],
  dressing: {}
}

export interface BrandAppPointsConfig {
  singular: string
  plural: string
  emoji: string | null
}

export interface BrandAppIdentity {
  brandName: string
  logo: string
  pwaIconUrl?: string
  primary: string
  buttonColor?: string
  fontBody?: BrandAppFont | null
  fontHeading?: BrandAppFont | null
  usePoliteLanguage: boolean
  pointsConfig: BrandAppPointsConfig
}

export const DEFAULT_IDENTITY: BrandAppIdentity = {
  brandName: '',
  logo: '',
  pwaIconUrl: undefined,
  primary: '#0060A8',
  buttonColor: undefined,
  fontBody: undefined,
  fontHeading: undefined,
  usePoliteLanguage: true,
  pointsConfig: { singular: 'point', plural: 'points', emoji: null }
}

export type BrandAppSocialId = 'instagram' | 'tiktok' | 'facebook' | 'youtube' | 'website'

export interface BrandAppSocialDef {
  id: BrandAppSocialId
  label: string
  placeholder: string
}

export const BRAND_APP_SOCIALS: BrandAppSocialDef[] = [
  {
    id: 'instagram',
    label: 'Instagram',
    get placeholder() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.instagram.placeholder')
    }
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    get placeholder() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.tiktok.placeholder')
    }
  },
  {
    id: 'facebook',
    label: 'Facebook',
    get placeholder() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.facebook.placeholder')
    }
  },
  {
    id: 'youtube',
    label: 'YouTube',
    get placeholder() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.youtube.placeholder')
    }
  },
  {
    id: 'website',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.website.label')
    },
    get placeholder() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.socials.website.placeholder')
    }
  }
]

export interface BrandAppSocials {
  instagram: string
  tiktok: string
  facebook: string
  youtube: string
  website: string
}

export const DEFAULT_SOCIALS: BrandAppSocials = {
  instagram: '',
  tiktok: '',
  facebook: '',
  youtube: '',
  website: ''
}

export type BrandAppOrderChannelId = 'clickAndCollect' | 'uberEats' | 'deliveroo' | 'onSite'

export const BRAND_APP_ORDER_CHANNELS: { id: BrandAppOrderChannelId; label: string; urlLabel: string }[] = [
  {
    id: 'clickAndCollect',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.clickAndCollect.label')
    },
    get urlLabel() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.clickAndCollect.urlLabel')
    }
  },
  {
    id: 'uberEats',
    label: 'Uber Eats',
    get urlLabel() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.uberEats.urlLabel')
    }
  },
  {
    id: 'deliveroo',
    label: 'Deliveroo',
    get urlLabel() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.deliveroo.urlLabel')
    }
  },
  {
    id: 'onSite',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.onSite.label')
    },
    get urlLabel() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.order.channels.onSite.urlLabel')
    }
  }
]

export interface BrandAppOrderChannel {
  enabled: boolean
  perStore: boolean
  url: string
  storeUrls: Record<number, string>
}

export interface BrandAppOrder {
  catalogUrl: string
  channels: Record<BrandAppOrderChannelId, BrandAppOrderChannel>
}

export const createOrderChannel = (): BrandAppOrderChannel => ({
  enabled: false,
  perStore: false,
  url: '',
  storeUrls: {}
})

export const createDefaultOrder = (): BrandAppOrder => ({
  catalogUrl: '',
  channels: {
    clickAndCollect: createOrderChannel(),
    uberEats: createOrderChannel(),
    deliveroo: createOrderChannel(),
    onSite: createOrderChannel()
  }
})

export type BrandAppStoreActionId = 'directions' | 'order' | 'call'

export const BRAND_APP_STORE_ACTIONS: { id: BrandAppStoreActionId; label: string; source: string; missing: string }[] =
  [
    {
      id: 'directions',
      get label() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.directions.label')
      },
      get source() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.directions.source')
      },
      get missing() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.directions.missing')
      }
    },
    {
      id: 'order',
      get label() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.order.label')
      },
      get source() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.order.source')
      },
      get missing() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.order.missing')
      }
    },
    {
      id: 'call',
      get label() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.call.label')
      },
      get source() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.call.source')
      },
      get missing() {
        return i18n.global.t('pages.loyaltySupports.webapp.editor.stores.actions.call.missing')
      }
    }
  ]

export interface BrandAppStoresScreen {
  hiddenStoreIds: number[]
  actions: BrandAppStoreActionId[]
}

export const DEFAULT_STORES_SCREEN: BrandAppStoresScreen = {
  hiddenStoreIds: [],
  actions: ['directions', 'order']
}

export type CompletionFieldId = 'firstName' | 'lastName' | 'email' | 'gender' | 'birthdate' | 'postalCode'

export interface CompletionFieldDef {
  id: CompletionFieldId
  label: string
}

export const COMPLETION_FIELDS: CompletionFieldDef[] = [
  {
    id: 'firstName',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.firstName.label')
    }
  },
  {
    id: 'lastName',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.lastName.label')
    }
  },
  {
    id: 'email',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.email.label')
    }
  },
  {
    id: 'gender',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.gender.label')
    }
  },
  {
    id: 'birthdate',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.birthdate.label')
    }
  },
  {
    id: 'postalCode',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.completion.postalCode.label')
    }
  }
]

export const DEFAULT_COMPLETION_FIELDS: CompletionFieldId[] = COMPLETION_FIELDS.map((field) => field.id)

export const completionFieldsFromApi = (saved: string[] | null | undefined): CompletionFieldId[] => {
  const fields = DEFAULT_COMPLETION_FIELDS.filter((id) => saved?.includes(id))

  return fields.length > 0 ? fields : [...DEFAULT_COMPLETION_FIELDS]
}

export type LoyaltyBlockId = 'unlocked' | 'missions' | 'catalog'

export interface LoyaltyBlockDef {
  id: LoyaltyBlockId
  label: string
}

export const LOYALTY_BLOCKS: LoyaltyBlockDef[] = [
  {
    id: 'unlocked',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.loyaltyBlocks.unlocked.label')
    }
  },
  {
    id: 'missions',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.loyaltyBlocks.missions.label')
    }
  },
  {
    id: 'catalog',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.loyaltyBlocks.catalog.label')
    }
  }
]

export const DEFAULT_LOYALTY_BLOCKS: LoyaltyBlockId[] = LOYALTY_BLOCKS.map((block) => block.id)

export const loyaltyBlocksFromApi = (saved: string[] | null | undefined): LoyaltyBlockId[] => {
  const known = (saved ?? []).filter((id): id is LoyaltyBlockId =>
    DEFAULT_LOYALTY_BLOCKS.includes(id as LoyaltyBlockId)
  )

  return [...new Set([...known, ...DEFAULT_LOYALTY_BLOCKS])]
}

export interface BrandAppRegistration {
  askCompletion: boolean
  completionFields: CompletionFieldId[]
}

export type BrandAppTabId = 'home' | 'order' | 'loyalty' | 'stores'

export type BrandAppNewsScreen = BrandAppTabId | 'profile' | 'wallet'

export type BrandAppNewsTarget =
  | { type: 'external'; url: string }
  | { type: 'screen'; screen: BrandAppNewsScreen }
  | { type: 'screen'; screen: 'survey'; surveyId: number | null }

export interface BrandAppNews {
  id: string
  image: string
  title: string
  description: string
  ctaLabel: string
  ctaTarget: BrandAppNewsTarget
  ctaUrl?: string
  isVisible?: boolean
  isPlaceholder?: boolean
}

export interface BrandAppTabDef {
  id: BrandAppTabId
  label: string
}

export const BRAND_APP_TABS: BrandAppTabDef[] = [
  {
    id: 'home',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.tabs.home')
    }
  },
  {
    id: 'loyalty',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.tabs.loyalty')
    }
  },
  {
    id: 'order',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.tabs.order')
    }
  },
  {
    id: 'stores',
    get label() {
      return i18n.global.t('pages.loyaltySupports.webapp.editor.contract.tabs.stores')
    }
  }
]

export const DEFAULT_TABS: BrandAppTabId[] = ['loyalty']

export interface BrandAppDerivedFeatures {
  showTabBar: boolean
  loyaltyLayout: 'tabs' | 'sections' | null
  challengesDisplay: 'carousel' | 'button' | null
  showProfileInLoyalty: boolean | null
  authStyle: 'bottom-sheet' | 'fullscreen'
}

export interface BrandAppShowcaseStore {
  id: number
  name: string
  address?: string
  city?: string
  phone?: string
}

export interface BrandAppPreviewOrder {
  menuUrl: string
  restaurantUrl: string
  catalogUrl: string
  orderOptions: { id: string; label: string; url: string }[]
}

export const channelUrlForStore = (channel: BrandAppOrderChannel, storeId: number) =>
  safeHttpUrl(channel.perStore ? channel.storeUrls[storeId] : channel.url)

export type BrandAppPreviewOrderOption = BrandAppPreviewOrder['orderOptions'][number]

export const storeOrderOptions = (order: BrandAppOrder, storeId: number): BrandAppPreviewOrderOption[] =>
  BRAND_APP_ORDER_CHANNELS.filter(({ id }) => order.channels[id].enabled)
    .map(({ id, label }) => ({ id, label, url: channelUrlForStore(order.channels[id], storeId) }))
    .filter((option) => option.url)

export const missingStoreActions = (
  store: BrandAppShowcaseStore,
  actions: BrandAppStoreActionId[],
  order: BrandAppOrder
): BrandAppStoreActionId[] =>
  actions.filter((action) => {
    if (action === 'directions') return !store.address
    if (action === 'call') return !store.phone

    return storeOrderOptions(order, store.id).length === 0
  })

export interface BrandAppPreviewFocus {
  kind: 'offer' | 'actu' | 'mission'
  id: string
  view: 'card' | 'detail'
}

export interface BrandAppMissionFocus {
  id: number | null
  type: MissionType | null
}

export const deriveFeatures = (tabs: BrandAppTabId[]): BrandAppDerivedFeatures => {
  const hasLoyalty = tabs.includes('loyalty')

  if (!hasLoyalty) {
    return {
      showTabBar: tabs.length > 1,
      loyaltyLayout: null,
      challengesDisplay: null,
      showProfileInLoyalty: null,
      authStyle: 'fullscreen'
    }
  }

  if (tabs.length === 1) {
    return {
      showTabBar: false,
      loyaltyLayout: 'tabs',
      challengesDisplay: 'carousel',
      showProfileInLoyalty: true,
      authStyle: 'bottom-sheet'
    }
  }

  return {
    showTabBar: true,
    loyaltyLayout: 'sections',
    challengesDisplay: 'button',
    showProfileInLoyalty: false,
    authStyle: 'fullscreen'
  }
}
